import React, { useEffect, useState, HTMLInputTypeAttribute } from 'react'
import { TextField } from '@pokemon/design.ui.input.text-field'
import { FieldInputProps } from 'react-final-form'
import { TFunction, useTranslation } from 'react-i18next'
import './password-field.css'
import checkMark from './check-mark-circle-solid.svg'
import xMark from './times-circle-solid.svg'

export interface Requirement {
  /** 
   * identification for password
   */
  id: string
  /** 
   * callback function for validating Requirement
   */
  validate: (text: string) => boolean
  /** 
   * boolean for determining visibility to users (requirements such as invalid characters appear as invisible to users)
   */
  visible: boolean
  /** 
   * string to display requirement to user
   */
  description: string
}

/** 
 * Props to use when initializing a `PasswordField` component.
 */
export interface MultiValidateFieldProps {
  /**
   * Array of requirements for the field to validate against.
   */
  validations: Requirement[]

  /**
   * Optional function to execute on text change.
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void

  /**
   * Optional function to execute on text validation.
   */
  onValidation?: (validation: TextValidationResult) => void

  /**
   * Error text message to display to user. By default, will not show.
   */
  errorText?: string

  /**
   * `FieldInputProps` from `react-final-form`
   */
  input?: FieldInputProps<string, HTMLElement>

  /**
   * Input type for the underlying text input - supported values include "text" and "password", although other HTML input types may function correctly.
   */
  inputType?: HTMLInputTypeAttribute
}

/**
 * The result of a password validation attempt.
 */
export interface TextValidationResult {
  /**
   * Password that was validated.
   */
  password: string

  /**
   * The validation result.
   */
  valid: boolean
}

export interface PasswordFieldProps {
  /**
  * Error text message to display to user. By default, will not show.
  */
  errorText?: string

  /**
   * `FieldInputProps` from `react-final-form`
   */
  input?: FieldInputProps<string, HTMLElement>

  /**
   * Optional function to execute on text change.
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void

  /**
   * Optional function to execute on text validation.
   */
  onValidation?: (validation: TextValidationResult) => void
}

const passwordFieldValidations = (t: TFunction<"translation", undefined>) => {
  return [
    {
      id: 'length',
      visible: true,
      validate: (pass: string) => pass != null && pass != undefined && pass.length >= 8 && pass.length <= 50,
      description: t('password_requirement_length', "8 to 50 characters")
    },
    {
      id: 'uppercase',
      visible: true,
      validate: (pass: string) => /[A-Z]/.test(pass),
      description: t('password_requirement_uppercase', "One uppercase")
    },
    {
      id: 'lowercase',
      visible: true,
      validate: (pass: string) => /[a-z]/.test(pass),
      description: t('password_requirement_lowercase', "One lowercase")
    },
    {
      id: 'number',
      visible: true,
      validate: (pass: string) => /[0-9]/.test(pass),
      description: t('password_requirement_number', "One number")
    },
    {
      id: 'special',
      visible: true,
      validate: (pass: string) => /[^a-zA-Z0-9\s]/.test(pass),
      description: t('password_requirement_special_char', "One special character")
    },
    {
      id: 'invalid-wide-chars',
      visible: false,
      validate: (pass: string) =>
      !/[^\p{Ll}\p{Lm}\p{Lo}\p{Lt}\p{Lu}\p{Mc}\p{Me}\p{Mn}\p{Nd}\p{Nl}\p{No}\p{Pc}\p{Pd}\p{Pe}\p{Pf}\p{Pi}\p{Po}\p{Ps}\p{Sc}\p{Sk}\p{Sm}\p{So}\p{Zs}]|[\u3000\uFF01-\uFF5E]/u.test(pass),
      description: t('password_requirement_invalid_char', "Password only contains valid characters")
    },

  ] as Requirement[]
}

export const validatePassword = (password: string) => {
  return composeValidations(passwordFieldValidations(() => ""), password)  
}

const composeValidations = (validations: Requirement[], password: string) => {
  return validations.every(requirement => {
    return requirement.validate(password) 
  })
}

export function PasswordField({
  errorText,
  input,
  onValidation
}: PasswordFieldProps) {
  const {t} = useTranslation()
  return <MultiValidateField
    validations={passwordFieldValidations(t)}
    errorText={errorText}
    inputType={"password"}
    input={input}
    onValidation={onValidation}
  />
}

/**
 * A field allowing trainers to enter their password, which is then validated against a set of rules.
 */
export function MultiValidateField({
  validations,
  onValidation = () => { },
  errorText,
  input,
  inputType = "text"
}: MultiValidateFieldProps) {
  
  const {t} = useTranslation()

  const [password, setPassword] = useState('')
  const [validationResults, setValidationResults] = useState(new Array(validations.length).fill(true))

  useEffect(() => {
    onValidation({ password: password, valid: composeValidations(validations, password) })

  }, [password])

  useEffect(() => {
    let results = validations.map(requirement => {
      return requirement.validate(password)
    })
    setValidationResults(results)
  }, [validations, password])

  /**
   * Execute on password change events
   */
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // iterate through failed requirements and mark found requirements as false
    setPassword(event.target.value)
    if (input) {
      input.onChange(event)
    }
  }

  // Get list of failed validation HTML IDs
  const failedValidationIds = validations
    .filter((_, i) => !validationResults[i])
    .map((v) => `password-field-validation-${v.id}`)
  const validationDescribedBy = failedValidationIds.length > 0 ? failedValidationIds.join(" ") : undefined
  
  return (
    <div className='password-field' data-testid='password-field'>
      <TextField 
        className="field" 
        name="password" 
        placeholder="Password" 
        type={inputType} 
        onChange={onChange} 
        errorText={errorText} 
        aria-invalid={failedValidationIds.length > 0} 
        aria-describedby={validationDescribedBy} />
      <div className="password-requirements">
        {
          validations.map((requirement, index) => {
            const valid = validationResults[index]
            if (!valid || requirement.visible) {
              return <div className="requirement" id={`password-field-validation-${requirement.id}`} key={requirement.description}>
                <img className="mark" src={valid ? checkMark : xMark} alt={valid ? t('requirement_met', "Requirement met") : t("requirement_not_met", "Requirement not met")}/>
                <p id={requirement.description} className={valid ? "checked" : ""}>{requirement.description}</p>
              </div>
            } else {
              return null
            }
          })
        }
      </div>
    </div>
  )
}
